import { InMemoryCache } from '@apollo/client';

import { AuthenticationResult } from '@quality24/inpatient-typings';
import {
  logout,
  parseAuthFlowResult,
  refreshAuthToken,
  setAuthPayload,
} from './auth';
import subscriptionClient, {
  defaultWebSocketState as wsState,
  WebSocketState,
} from './websocket';

import { AuthPayload } from '../@types/auth';
import { getAdmissionId } from './storage';

// //////////////////////////////////////////////
// Cache definitions
// //////////////////////////////////////////////

// Create cache
export const cache = new InMemoryCache();

/**
 * Resets the apollo-client cache
 */
export const resetCache = async (): Promise<void> => {
  await cache.reset();
};

// //////////////////////////////////////////////
// Maintenance mode
// //////////////////////////////////////////////

/** Refresh retry delay when Retry-After is missing or can't be parsed */
const MAINTENANCE_DEFAULT_RETRY_MS = 60000;

/**
 * Bounds for the refresh retry delay during maintenance: keeps the app from
 * hammering the API and from waiting hours when the window ends early
 */
const MAINTENANCE_MIN_RETRY_MS = 5000;
const MAINTENANCE_MAX_RETRY_MS = 60000;

/** Retry delay for any other refresh failure */
const DEFAULT_RETRY_MS = 5000;

class MaintenanceModeError extends Error {
  retryAfterMs: number;

  constructor(retryAfterMs: number) {
    super('<retryRefreshToken> In maintenance mode');
    this.name = 'MaintenanceModeError';
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Converts a Retry-After header (seconds or HTTP date) into the delay before
 * the next refresh attempt
 */
export const getMaintenanceRetryDelay = (retryAfter: string | null): number => {
  if (!retryAfter) return MAINTENANCE_DEFAULT_RETRY_MS;

  const seconds = Number(retryAfter);
  const delay = Number.isNaN(seconds)
    ? Date.parse(retryAfter) - Date.now()
    : seconds * 1000;
  if (Number.isNaN(delay)) return MAINTENANCE_DEFAULT_RETRY_MS;

  return Math.min(
    Math.max(delay, MAINTENANCE_MIN_RETRY_MS),
    MAINTENANCE_MAX_RETRY_MS,
  );
};

let maintenanceModeListener: (() => void) | null = null;

/**
 * Registers the callback fired when a token refresh hits the maintenance mode.
 * The refresh runs outside React, so the UI hooks in through here.
 * @returns a function that removes the listener
 */
export const setMaintenanceModeListener = (
  listener: () => void,
): (() => void) => {
  maintenanceModeListener = listener;
  return () => {
    if (maintenanceModeListener === listener) maintenanceModeListener = null;
  };
};

// //////////////////////////////////////////////
// Custom fetch definition
// //////////////////////////////////////////////

/**
 * Stores the promise of a refresh operation.
 * Note: store it globally in order not to run multiple
 * refreshes at the same time
 */

export const retryRefreshToken = (
  uri: string,
  refreshToken: string,
): Promise<AuthPayload | void | null> => {
  if (!refreshToken) {
    // eslint-disable-next-line no-console
    console.error(
      'Failed to refresh token. refreshToken is required but was not provided',
    );
    // Clear auth data to force a logout
    resetCache();
    logout();
    return Promise.resolve(null);
  }

  return refreshAuthToken(uri, refreshToken)
    .then(async (res) => {
      // Maintenance mode: keep the session and retry once the window allows.
      // A 503 without Retry-After is not ours (e.g. load balancer), so it
      // falls back to the regular retry instead of logging the user out.
      if (res.status === 503) {
        const retryAfter = res.headers.get('Retry-After');
        if (retryAfter === null) {
          throw new Error('Failed to refresh token: service unavailable');
        }
        maintenanceModeListener?.();
        throw new MaintenanceModeError(getMaintenanceRetryDelay(retryAfter));
      }

      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.error(
          `Failed to refresh token, logging user out. 
          Request status: ${res.status} - ${res.statusText}`,
        );

        // Clear auth data to force a logout
        await resetCache();
        logout();
        return false;
      }

      return res.json();
    })
    .then(async (json) => {
      // Get the auth data
      const authResult = json.data?.authFlow
        ?.authenticationResult as AuthenticationResult;
      if (!authResult) {
        // eslint-disable-next-line no-console
        console.error(
          'Could not refresh tokens using refreshToken. Logging user out',
        );
        // Clear auth data to force a logout
        await resetCache();
        logout();
        return null;
      }

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('graphql: ran retryRefreshToken', authResult);
      }

      // Save new auth payload
      const authPayload = parseAuthFlowResult({
        ...authResult,
        /**
         * Temporarily bypass admissionId
         * This might be removed after refreshDeviceTokenWithType is updated in the API to return the admissionId
         */
        userData: { ...authResult.userData, admissionId: getAdmissionId() },
      });
      setAuthPayload(authPayload);

      // If previous WebSocket connection was closed, force a reconnection
      // after refreshing the token
      if (wsState.state === WebSocketState.DISCONNECTED) {
        subscriptionClient.terminate();
      }
      return authPayload;
    })
    .catch(async (err) => {
      const retryIn =
        err instanceof MaintenanceModeError
          ? err.retryAfterMs
          : DEFAULT_RETRY_MS;

      // eslint-disable-next-line no-console
      console.error(`Failed to refresh token, will retry in ${retryIn}ms`, err);
      // Only retry if refreshToken is still available
      setTimeout(() => {
        if (refreshToken) {
          retryRefreshToken(uri, refreshToken);
        }
      }, retryIn);
    });
};
